import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import SpeakerButton from "@/components/ui/SpeakerButton";
import useSpeech from "@/hooks/useSpeech";
import useExerciseEngine from "@/hooks/useExerciseEngine";
import { cn } from "@/utils/utils";

export default function CompleteTheConversationExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";
  const { speak, stop } = useSpeech();

  /* ================= SAFE QUESTIONS ================= */
  const questionsRaw = Array.isArray(exercise?.conversationQuestions)
    ? exercise.conversationQuestions
    : [];

  /* ================= ENGINE QUESTIONS ================= */
  const engineQuestions = questionsRaw.map((q) => ({
    type: "single",
    answer: q.answer,
    requireCapitalization: q.requireCapitalization,
    skill: "conversation",
  }));

  /* ================= SAFE EXTERNAL ================= */
  const safeExternalAnswers =
    typeof userAnswer?.answer === "object" ? userAnswer.answer : {};

  /* ================= ENGINE ================= */
  const { answers, setAnswer, getInputState } = useExerciseEngine({
    questions: engineQuestions,
    showResult,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC TO PARENT (CRITICAL) ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers, isPreview, onComplete]);

  /* ================= SPEECH ================= */
  const sanitizeForSpeech = (text = "") =>
    String(text)
      .replace(/_{2,}/g, " blank ")
      .replace(/_/g, " underscore ")
      .replace(/\s+/g, " ")
      .trim();

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questionsRaw.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-5">
            <TextareaField
              label={`Line ${idx + 1}`}
              value={q.question || ""}
              onChange={(v) =>
                onUpdate("conversationQuestions", [
                  ...questionsRaw.slice(0, idx),
                  { ...q, question: v },
                  ...questionsRaw.slice(idx + 1),
                ])
              }
            />

            <StringField
              label="Correct Answer"
              value={q.answer || ""}
              onChange={(v) =>
                onUpdate("conversationQuestions", [
                  ...questionsRaw.slice(0, idx),
                  { ...q, answer: v },
                  ...questionsRaw.slice(idx + 1),
                ])
              }
            />

            <CapitalizationRule
              checked={!!q.requireCapitalization}
              onChange={(c) =>
                onUpdate("conversationQuestions", [
                  ...questionsRaw.slice(0, idx),
                  { ...q, requireCapitalization: c },
                  ...questionsRaw.slice(idx + 1),
                ])
              }
            />

            <Button
              variant="destructive"
              onClick={() =>
                onUpdate(
                  "conversationQuestions",
                  questionsRaw.filter((_, i) => i !== idx)
                )
              }
            >
              Remove
            </Button>
          </Card>
        ))}

        <Button
          onClick={() =>
            onUpdate("conversationQuestions", [
              ...questionsRaw,
              { question: "", answer: "", requireCapitalization: false },
            ])
          }
        >
          Add Line
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questionsRaw.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Complete the Conversation</CardTitle>
        <CardDescription>
          Fill in the missing answers
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questionsRaw.map((q, idx) => {
          const value = answers[idx] || "";
          const inputState = getInputState(value, q.answer);

          return (
            <div
              key={idx}
              className="rounded-xl border bg-muted/40 p-6 space-y-6"
            >
              {/* QUESTION */}
              <pre className="whitespace-pre-wrap text-base">
                {q.question}
              </pre>

              {/* SPEAKER */}
              <div className="flex justify-center">
                <SpeakerButton
                  text={sanitizeForSpeech(q.question)}
                  onPlay={() => speak(sanitizeForSpeech(q.question))}
                  onStop={stop}
                />
              </div>

              {/* INPUT */}
              <Input
                value={value}
                onChange={(e) => {
                  setAnswer(idx, e.target.value);
                }}
                disabled={showResult}
                placeholder="Type answer..."
                className={cn(
                  "text-center text-lg",
                  inputState.className
                )}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}